import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { percent, getSummaryData } from '../utils/formatters';
import { PERFORMANCE_YEAR } from '../utils/newApi';

Chart.register(...registerables, annotationPlugin);

// Set default font for all charts
Chart.defaults.font.family = "'Montserrat', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
Chart.defaults.font.size = 12;

// Helper function to extract all centers' volume data
const extractAllCentersVolumeData = (summary, labels) => {
  if (!summary || !Array.isArray(summary)) return [];
  
  // Map the year labels to column names
  const yearColumns = labels.map(label => `${label} - Total Transplants`);
  
  return summary
    .map(center => {
      const centerId = center['Center Code'];
      const centerName = center['Center Name'];
      
      // Extract volume data for each year
      const volumes = yearColumns.map(col => {
        const val = Number(center[col]);
        return Number.isFinite(val) ? val : null;
      });
      
      // Only include centers with at least some data
      const hasData = volumes.some(v => v !== null);
      if (!hasData) return null;
      
      return {
        centerId,
        centerName,
        volumes
      };
    })
    .filter(center => center !== null);
};

function VisualizationPanel({ response, inputs }) {
  const accChartRef = useRef(null);
  const graftChartRef = useRef(null);
  const volumeChartRef = useRef(null);
  const accChartInstance = useRef(null);
  const graftChartInstance = useRef(null);
  const volumeChartInstance = useRef(null);
  const [isVolumeExpanded, setIsVolumeExpanded] = useState(false);

  useEffect(() => {
    if (accChartRef.current) {
      const ctx = accChartRef.current.getContext('2d');
      accChartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: [],
          datasets: [
            { label: 'National distribution', data: [], backgroundColor: '#0e4f56', borderRadius: 6, barThickness: 25 }
          ]
        },
        options: {
          animation: { duration: 700 },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}`
              }
            },
            annotation: {
              annotations: {}
            }
          },
          scales: {
            x: { 
              grid: { display: false }, 
              ticks: { 
                color: 'rgba(0,0,0,0.6)',
                font: { size: 12 }
              } 
            },
            y: { 
              grid: { color: 'rgba(0,0,0,0.08)' }, 
              ticks: { 
                color: 'rgba(0,0,0,0.6)',
                font: { size: 12 }
              } 
            }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      });
    }

    if (graftChartRef.current) {
      const ctx = graftChartRef.current.getContext('2d');
      graftChartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: [],
          datasets: [
            { label: 'National distribution', data: [], backgroundColor: '#0e4f56', borderRadius: 6, barThickness: 25 }
          ]
        },
        options: {
          animation: { duration: 700 },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}`
              }
            },
            annotation: {
              annotations: {}
            }
          },
          scales: {
            x: { 
              grid: { display: false }, 
              ticks: { 
                color: 'rgba(0,0,0,0.6)',
                font: { size: 12 }
              } 
            },
            y: { 
              grid: { color: 'rgba(0,0,0,0.08)' }, 
              ticks: { 
                color: 'rgba(0,0,0,0.6)',
                font: { size: 12 }
              } 
            }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      });
    }

    if (volumeChartRef.current) {
      const ctx = volumeChartRef.current.getContext('2d');
      volumeChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: [],
          datasets: [
            {
              label: 'Actual Transplants',
              data: [],
              borderColor: '#0e4f56',
              backgroundColor: 'rgba(14, 79, 86, 0.1)',
              borderWidth: 2,
              fill: true,
              tension: 0.3,
              pointRadius: 3,
              pointHoverRadius: 5
            },
            {
              label: 'Performance Year 1',
              data: [],
              borderColor: '#cd7029',
              backgroundColor: 'rgba(205, 112, 41, 0.1)',
              borderWidth: 2,
              borderDash: [5, 5],
              fill: true,
              tension: 0.3,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointStyle: 'rectRot'
            },
            {
              label: 'Target',
              data: [],
              borderColor: '#666666',
              borderWidth: 2,
              borderDash: [8, 4],
              pointRadius: 0,
              fill: false
            }
          ]
        },
        options: {
          animation: { duration: 700 },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: { 
                color: 'rgba(0,0,0,0.7)', 
                boxWidth: 30, 
                padding: 12,
                font: { size: 12 },
                // Only show main datasets in legend (not background centers)
                filter: function(legendItem, chartData) {
                  // Show only: Actual Transplants, Performance Year 1, and Target
                  const mainLabels = ['Actual Transplants', 'Performance Year 1', 'Target'];
                  return mainLabels.includes(legendItem.text);
                }
              }
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  if (ctx.raw === null || ctx.raw === undefined) return '';
                  const label = ctx.dataset.label || '';
                  const value = Math.round(ctx.raw);
                  return `${label}: ${value}`;
                },
                title: (ctx) => {
                  if (ctx.length > 0) {
                    return ctx[0].label;
                  }
                  return '';
                }
              },
              filter: function(tooltipItem) {
                // Only show main datasets in tooltip (exclude background center lines)
                const mainLabels = ['Actual Transplants', 'Performance Year 1', 'Target'];
                return mainLabels.includes(tooltipItem.dataset.label);
              },
              intersect: false,
              mode: 'index'
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                color: 'rgba(0,0,0,0.6)',
                font: { size: 12 },
                maxRotation: 45,
                minRotation: 45
              }
            },
            y: {
              grid: { color: 'rgba(0,0,0,0.08)' },
              ticks: { 
                color: 'rgba(0,0,0,0.6)',
                font: { size: 12 }
              }
            }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      });
    }

    return () => {
      if (accChartInstance.current) accChartInstance.current.destroy();
      if (graftChartInstance.current) graftChartInstance.current.destroy();
      if (volumeChartInstance.current) volumeChartInstance.current.destroy();
    };
  }, []);

  const findBinIndex = useCallback((bins, centerValue) => {
    // Find the closest bin to the center value
    let closestIndex = 0;
    let closestDiff = Infinity;
    
    for (let i = 0; i < bins.length; i++) {
      const binValue = Number(bins[i]);
      if (!isNaN(binValue)) {
        const diff = Math.abs(binValue - centerValue);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = i;
        }
      }
    }
    
    return closestIndex;
  }, []);

  const updateChart = useCallback((chart, bins, freqArray, centerIndex = null, centerValue = null) => {
    chart.data.labels = bins;
    chart.data.datasets[0].data = freqArray;
    
    // Remove previous annotation if it exists
    if (chart.options.plugins.annotation) {
      chart.options.plugins.annotation.annotations = {};
    }
    
    // Add vertical line annotation if center position is known
    if (centerIndex !== null && centerIndex >= 0 && centerIndex < bins.length) {
      chart.options.plugins.annotation.annotations.centerLine = {
        type: 'line',
        xMin: centerIndex,
        xMax: centerIndex,
        borderColor: '#cd7029',
        borderWidth: 3,
        label: {
          display: true,
          content: `${inputs.centerId}: ${centerValue !== null ? centerValue.toFixed(1) : bins[centerIndex]}`,
          position: 'start',
          backgroundColor: '#cd7029',
          color: '#ffffff',
          font: {
            size: 11,
            weight: 'bold'
          },
          padding: 4,
          yAdjust: -10
        }
      };
    }
    
    // Use a short, smooth animation for responsive feedback
    chart.update({
      duration: 250,
      easing: 'easeInOutQuart'
    });
  }, [inputs.centerId]);

  const updateVolumeChart = useCallback((chart, labels, volumes, projected, target, allCentersData = null, currentCenterId = null) => {
    chart.data.labels = labels;
    
    // Track how many background center datasets we need
    const numBackgroundCenters = (isVolumeExpanded && allCentersData) 
      ? allCentersData.filter(c => c.centerId !== currentCenterId).length 
      : 0;
    
    // Main datasets: Actual Transplants, Performance Year 1, Target
    const mainDatasetStartIndex = numBackgroundCenters;
    
    // Update or create background center datasets
    if (isVolumeExpanded && allCentersData && allCentersData.length > 0) {
      let bgIndex = 0;
      allCentersData.forEach((centerData) => {
        const isCurrent = centerData.centerId === currentCenterId;
        if (!isCurrent) {
          if (bgIndex < chart.data.datasets.length && bgIndex < mainDatasetStartIndex) {
            // Update existing background dataset
            chart.data.datasets[bgIndex].data = centerData.volumes;
            chart.data.datasets[bgIndex].label = centerData.centerName || centerData.centerId;
            chart.data.datasets[bgIndex].borderColor = 'rgba(150, 150, 150, 0.15)';
            chart.data.datasets[bgIndex].backgroundColor = 'transparent';
            chart.data.datasets[bgIndex].borderWidth = 1;
            chart.data.datasets[bgIndex].fill = false;
            chart.data.datasets[bgIndex].tension = 0.3;
            chart.data.datasets[bgIndex].pointRadius = 0;
            chart.data.datasets[bgIndex].pointHoverRadius = 0;
            chart.data.datasets[bgIndex].pointHitRadius = 0;
          } else {
            // Create new background dataset if needed
            chart.data.datasets.splice(bgIndex, 0, {
              label: centerData.centerName || centerData.centerId,
              data: centerData.volumes,
              borderColor: 'rgba(150, 150, 150, 0.15)',
              backgroundColor: 'transparent',
              borderWidth: 1,
              fill: false,
              tension: 0.3,
              pointRadius: 0,
              pointHoverRadius: 0,
              pointHitRadius: 0,
              hidden: false
            });
          }
          bgIndex++;
        }
      });
      
      // Remove extra background datasets if we have fewer centers now
      while (chart.data.datasets.length > bgIndex && bgIndex < mainDatasetStartIndex) {
        chart.data.datasets.splice(bgIndex, 1);
      }
    } else {
      // Remove all background center datasets when not expanded
      chart.data.datasets.splice(0, mainDatasetStartIndex);
    }
    
    // Recalculate main dataset start index after background dataset changes
    const updatedMainStart = (isVolumeExpanded && allCentersData) 
      ? allCentersData.filter(c => c.centerId !== currentCenterId).length 
      : 0;
    
    // Update or create "Actual Transplants" dataset
    const actualData = [...volumes];
    const actualIndex = updatedMainStart;
    if (actualIndex < chart.data.datasets.length && chart.data.datasets[actualIndex].label === 'Actual Transplants') {
      chart.data.datasets[actualIndex].data = actualData;
      chart.data.datasets[actualIndex].borderWidth = isVolumeExpanded ? 3 : 2;
      chart.data.datasets[actualIndex].borderColor = '#0e4f56';
      chart.data.datasets[actualIndex].backgroundColor = isVolumeExpanded ? 'transparent' : 'rgba(14, 79, 86, 0.1)';
      chart.data.datasets[actualIndex].fill = !isVolumeExpanded;
      chart.data.datasets[actualIndex].pointRadius = 3;
      chart.data.datasets[actualIndex].pointHoverRadius = 5;
    } else {
      chart.data.datasets.splice(actualIndex, 0, {
        label: 'Actual Transplants',
        data: actualData,
        borderColor: '#0e4f56',
        backgroundColor: isVolumeExpanded ? 'transparent' : 'rgba(14, 79, 86, 0.1)',
        borderWidth: isVolumeExpanded ? 3 : 2,
        fill: !isVolumeExpanded,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5
      });
    }
    
    // Update or create "Performance Year 1" dataset
    const projectedData = new Array(labels.length).fill(null);
    if (volumes.length > 0 && projected && projected.length > 0) {
      projectedData[volumes.length - 1] = volumes[volumes.length - 1];
      projectedData[volumes.length] = projected[0];
    }
    const projectedIndex = updatedMainStart + 1;
    if (projectedIndex < chart.data.datasets.length && chart.data.datasets[projectedIndex].label === 'Performance Year 1') {
      chart.data.datasets[projectedIndex].data = projectedData;
      chart.data.datasets[projectedIndex].borderWidth = isVolumeExpanded ? 1 : 2;
      chart.data.datasets[projectedIndex].borderColor = isVolumeExpanded ? 'rgba(205, 112, 41, 0.25)' : '#cd7029';
      chart.data.datasets[projectedIndex].backgroundColor = isVolumeExpanded ? 'transparent' : 'rgba(205, 112, 41, 0.1)';
      chart.data.datasets[projectedIndex].fill = !isVolumeExpanded;
      chart.data.datasets[projectedIndex].borderDash = [5, 5];
      chart.data.datasets[projectedIndex].tension = 0.3;
      chart.data.datasets[projectedIndex].pointRadius = 4;
      chart.data.datasets[projectedIndex].pointHoverRadius = 6;
      chart.data.datasets[projectedIndex].pointStyle = 'rectRot';
    } else {
      chart.data.datasets.splice(projectedIndex, 0, {
        label: 'Performance Year 1',
        data: projectedData,
        borderColor: isVolumeExpanded ? 'rgba(205, 112, 41, 0.25)' : '#cd7029',
        backgroundColor: isVolumeExpanded ? 'transparent' : 'rgba(205, 112, 41, 0.1)',
        borderWidth: isVolumeExpanded ? 1 : 2,
        borderDash: [5, 5],
        fill: !isVolumeExpanded,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointStyle: 'rectRot'
      });
    }
    
    // Update or create "Target" dataset
    const targetData = new Array(labels.length).fill(target);
    const targetIndex = updatedMainStart + 2;
    if (targetIndex < chart.data.datasets.length && chart.data.datasets[targetIndex].label === 'Target') {
      chart.data.datasets[targetIndex].data = targetData;
      chart.data.datasets[targetIndex].borderColor = isVolumeExpanded ? 'rgba(102, 102, 102, 0.6)' : '#666666';
      chart.data.datasets[targetIndex].borderWidth = isVolumeExpanded ? 1.5 : 2;
      chart.data.datasets[targetIndex].borderDash = [8, 4];
      chart.data.datasets[targetIndex].pointRadius = 0;
      chart.data.datasets[targetIndex].fill = false;
    } else {
      chart.data.datasets.splice(targetIndex, 0, {
        label: 'Target',
        data: targetData,
        borderColor: isVolumeExpanded ? 'rgba(102, 102, 102, 0.6)' : '#666666',
        borderWidth: isVolumeExpanded ? 1.5 : 2,
        borderDash: [8, 4],
        pointRadius: 0,
        fill: false
      });
    }
    
    // Remove any extra datasets beyond what we need
    const expectedLength = updatedMainStart + 3;
    while (chart.data.datasets.length > expectedLength) {
      chart.data.datasets.pop();
    }
    
    // Calculate min and max values for y-axis with one additional tick increment
    const allValues = [
      ...volumes.filter(v => v !== null && v !== undefined),
      ...(projected || []).filter(v => v !== null && v !== undefined),
      target
    ].filter(v => v !== null && v !== undefined);
    
    // Also include values from all centers if expanded
    if (isVolumeExpanded && allCentersData) {
      allCentersData.forEach(centerData => {
        centerData.volumes.forEach(v => {
          if (v !== null && v !== undefined) allValues.push(v);
        });
      });
    }
    
    if (allValues.length > 0) {
      const minValue = Math.min(...allValues);
      const maxValue = Math.max(...allValues);
      const range = maxValue - minValue;
      
      // Calculate a nice tick step size (Chart.js typically uses 5-10 ticks)
      // Using ~8 ticks as a target for nice spacing
      const roughStep = range / 8;
      
      // Round to a nice number (1, 2, 5, 10, 20, 50, 100, etc.)
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const normalized = roughStep / magnitude;
      let niceStep;
      if (normalized <= 1) niceStep = magnitude;
      else if (normalized <= 2) niceStep = 2 * magnitude;
      else if (normalized <= 5) niceStep = 5 * magnitude;
      else niceStep = 10 * magnitude;
      
      // Align min and max to nice tick boundaries without extra margin
      const alignedMin = Math.floor(minValue / niceStep) * niceStep;
      const alignedMax = Math.ceil(maxValue / niceStep) * niceStep;
      
      // Set y-axis min and max without additional tick margin
      chart.options.scales.y.min = Math.max(0, alignedMin); // Don't go below 0
      chart.options.scales.y.max = alignedMax;
    }
    
    // Use a short, smooth animation for responsive feedback
    chart.update({
      duration: 250,
      easing: 'easeInOutQuart'
    });
  }, [isVolumeExpanded]);

  useEffect(() => {
    if (response && accChartInstance.current && graftChartInstance.current && volumeChartInstance.current) {
      const acc = response.distribution?.acceptance;
      const graft = response.distribution?.graftSurvival;
      const volumeData = response.transplantVolume;

      if (acc && acc.bins) {
        const centerValue = Number(inputs.offerAcceptRate);
        const centerIndex = findBinIndex(acc.bins, centerValue);
        updateChart(accChartInstance.current, acc.bins, acc.freqs, centerIndex, centerValue);
      }

      if (graft && graft.bins) {
        const centerValue = Number(inputs.graftSurvival);
        const centerIndex = findBinIndex(graft.bins, centerValue);
        updateChart(graftChartInstance.current, graft.bins, graft.freqs, centerIndex, centerValue);
      }

      if (volumeData && volumeData.monthLabelsExpanded && volumeData.volumesExpanded) {
        // Always use expanded view as default (all years)
        const labels = volumeData.monthLabelsExpanded;
        const volumes = volumeData.volumesExpanded;
        
        // Get all centers' data if expanded
        let allCentersData = null;
        if (isVolumeExpanded && inputs.centerId) {
          const summaryData = getSummaryData();
          if (summaryData && summaryData.length > 0) {
            allCentersData = extractAllCentersVolumeData(summaryData, labels);
          }
        }
        
        updateVolumeChart(
          volumeChartInstance.current, 
          labels, 
          volumes, 
          volumeData.projected || [],
          volumeData.target,
          allCentersData,
          inputs.centerId
        );
      }
    }
  }, [response, inputs, updateChart, findBinIndex, updateVolumeChart, isVolumeExpanded]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
      {/* Top: Transplant Volume Trend - Full Width */}
      <div 
        style={{
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onClick={() => setIsVolumeExpanded(!isVolumeExpanded)}
      >
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <strong style={{ fontSize: '16px', color: '#0e4f56' }}>Transplant Volume Trend</strong>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transition: 'transform 0.3s ease',
                transform: isVolumeExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            >
              <path 
                d="M4 8L12 16L20 8" 
                stroke="#0e4f56" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div style={{textAlign: 'right'}}>
            <div className="small-muted">Last SRTR Reported Value</div>
            <div style={{fontWeight: '700', fontSize: '18px', color: '#0e4f56'}}>
              {response?.scores?.centerTransplants ? Math.round(response.scores.centerTransplants) : '--'}
            </div>
          </div>
        </div>
        <div style={{height: isVolumeExpanded ? '500px' : '400px', position: 'relative', transition: 'height 0.3s ease'}}>
          <canvas ref={volumeChartRef}></canvas>
        </div>
        {isVolumeExpanded && (
          <div style={{
            marginTop: '12px',
            padding: '10px 14px',
            backgroundColor: 'rgba(14, 79, 86, 0.05)',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'rgba(0,0,0,0.7)',
            textAlign: 'center'
          }}>
            Showing all transplant centers' volume trends. Your center is highlighted in teal.
          </div>
        )}
      </div>

      {/* Bottom: Side-by-side Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Offer Acceptance Rate */}
        <div>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px'}}>
            <div>
              <strong style={{ fontSize: '15px', color: '#0e4f56' }}>Offer Acceptance Rate Ratio</strong>
            </div>
            <div style={{textAlign: 'right'}}>
              <div className="small-muted">Last SRTR Value</div>
              <div style={{fontWeight: '700', fontSize: '16px', color: '#0e4f56'}}>
                {response?.scores?.centerOfferAcceptRate !== null && response?.scores?.centerOfferAcceptRate !== undefined 
                  ? response.scores.centerOfferAcceptRate.toFixed(2) 
                  : '--'}
              </div>
            </div>
          </div>
          <div style={{height: '220px', position: 'relative'}}>
            <canvas ref={accChartRef}></canvas>
          </div>
          <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '8px'}}>
            <div className="small-muted">Based on {PERFORMANCE_YEAR} SRTR Data</div>
            <div className="small-muted">Percentile: {response?.scores?.acceptancePercentile ? percent(response.scores.acceptancePercentile) : '--'}</div>
          </div>
        </div>

        {/* Graft Survival Rate */}
        <div>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px'}}>
            <div>
              <strong style={{ fontSize: '15px', color: '#0e4f56' }}>Graft Survival Rate (1yr)</strong>
            </div>
            <div style={{textAlign: 'right'}}>
              <div className="small-muted">Last SRTR Value</div>
              <div style={{fontWeight: '700', fontSize: '16px', color: '#0e4f56'}}>
                {response?.scores?.centerGraftSurvival !== null && response?.scores?.centerGraftSurvival !== undefined
                  ? percent(response.scores.centerGraftSurvival)
                  : '--'}
              </div>
            </div>
          </div>
          <div style={{height: '220px', position: 'relative'}}>
            <canvas ref={graftChartRef}></canvas>
          </div>
          <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '8px'}}>
            <div className="small-muted">Based on {PERFORMANCE_YEAR} SRTR Data</div>
            <div className="small-muted">Percentile: {response?.scores?.graftSurvivalPercentile ? percent(response.scores.graftSurvivalPercentile) : '--'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VisualizationPanel;
